import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { DateRange } from 'react-day-picker';
import { Calendar } from '../../../../components-v2/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { Button as ButtonV2 } from '../../../../components-v2/Button';
import { bs } from '../../../../styles/styles';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet } from './styles';

interface DatePreset {
	label: string;
	getDates: () => { from: string; to: string };
}

const DATE_PRESETS: DatePreset[] = [
	{
		label: 'Next 7 days',
		getDates: () => ({
			from: moment().format('YYYY-MM-DD'),
			to: moment().add(7, 'days').format('YYYY-MM-DD'),
		}),
	},
	{
		label: 'Next 14 days',
		getDates: () => ({
			from: moment().format('YYYY-MM-DD'),
			to: moment().add(14, 'days').format('YYYY-MM-DD'),
		}),
	},
	{
		label: 'Next 30 days',
		getDates: () => ({
			from: moment().format('YYYY-MM-DD'),
			to: moment().add(30, 'days').format('YYYY-MM-DD'),
		}),
	},
	{
		label: 'Next 90 days',
		getDates: () => ({
			from: moment().format('YYYY-MM-DD'),
			to: moment().add(90, 'days').format('YYYY-MM-DD'),
		}),
	},
];

export const MeetingDateFilterDropdown = ({
	selectedMinimumDate,
	setSelectedMinimumDate,
	selectedMaximumDate,
	setSelectedMaximumDate,
	anchorClassName: _anchorClassName,
}: {
	selectedMinimumDate: string | null;
	setSelectedMinimumDate: React.Dispatch<React.SetStateAction<string | null>>;
	selectedMaximumDate: string | null;
	setSelectedMaximumDate: React.Dispatch<React.SetStateAction<string | null>>;
	/** @deprecated No longer used. Kept for API compatibility. */
	anchorClassName?: string;
}) => {
	const triggerLabel = React.useMemo(() => {
		if (!selectedMinimumDate) return 'Start Date';
		if (selectedMinimumDate && selectedMaximumDate) {
			return `${moment(selectedMinimumDate).format('MM/DD')} - ${moment(selectedMaximumDate).format('MM/DD')}`;
		}
		return moment(selectedMinimumDate).format('MM/DD/YYYY');
	}, [selectedMinimumDate, selectedMaximumDate]);

	const selected: DateRange | undefined = React.useMemo(() => {
		if (!selectedMinimumDate) return undefined;
		return {
			from: moment(selectedMinimumDate).toDate(),
			to: selectedMaximumDate ? moment(selectedMaximumDate).toDate() : undefined,
		};
	}, [selectedMinimumDate, selectedMaximumDate]);

	const onSelect = (range: DateRange | undefined) => {
		if (!range) {
			setSelectedMinimumDate(null);
			setSelectedMaximumDate(null);
			return;
		}
		setSelectedMinimumDate(range.from ? moment(range.from).format('YYYY-MM-DD') : null);
		setSelectedMaximumDate(range.to ? moment(range.to).format('YYYY-MM-DD') : null);
	};

	const applyPreset = (preset: DatePreset) => {
		const { from, to } = preset.getDates();
		setSelectedMinimumDate(from);
		setSelectedMaximumDate(to);
	};

	const resetFilter = () => {
		setSelectedMinimumDate(null);
		setSelectedMaximumDate(null);
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button className={css(styleSheet.trigger)}>
					<span className={css(bs.truncateText)}>{triggerLabel}</span>
					<DisclosureIcon className={css(styleSheet.triggerIcon)} />
				</button>
			</PopoverTrigger>
			<PopoverContent align='start' styles={[styleSheet.content]}>
				<div className={css(styleSheet.layout)}>
					<div className={css(styleSheet.presets)}>
						{DATE_PRESETS.map(preset => (
							<button
								key={preset.label}
								className={css(styleSheet.presetButton)}
								onClick={() => applyPreset(preset)}
								type='button'
							>
								{preset.label}
							</button>
						))}
					</div>
					<Calendar mode='range' selected={selected} onSelect={onSelect} numberOfMonths={2} />
				</div>
				{selectedMinimumDate ? (
					<div className={css(styleSheet.resetContainer)}>
						<ButtonV2 variant='outline' size='sm' type='button' onClick={resetFilter}>
							Reset
						</ButtonV2>
					</div>
				) : null}
			</PopoverContent>
		</Popover>
	);
};
